import React, { useState, useEffect, useCallback } from 'react';
import { labAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const CATEGORIES = ['Haematology','Biochemistry','Serology','Urine','Stool','Hormones','Microbiology','Cardiac'];

const emptyForm = {
    test_id: '', parameter_name: '', display_order: 0, unit: '', decimal_places: 2,
    range_low: '', range_high: '', range_low_male: '', range_high_male: '',
    range_low_female: '', range_high_female: '', critical_low: '', critical_high: '',
    is_qualitative: false, qualitative_normal: '', method: ''
};

export default function LabParameterSettings() {
    const [params, setParams] = useState([]);
    const [tests, setTests] = useState([]);
    const [activeCategory, setActiveCategory] = useState('Haematology');
    const [showModal, setShowModal] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        try {
            const [pRes, tRes] = await Promise.all([labAPI.getAllParameters(), labAPI.getTests()]);
            setParams(pRes.data);
            setTests(tRes.data);
        } catch { toast.error('Failed to load parameters'); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = params.filter(p => {
        const catMatch = p.category === activeCategory;
        const searchMatch = !search || p.parameter_name.toLowerCase().includes(search.toLowerCase()) || p.test_name.toLowerCase().includes(search.toLowerCase());
        return catMatch && searchMatch;
    });

    // Group by test
    const grouped = filtered.reduce((acc, p) => {
        const key = p.test_name;
        if (!acc[key]) acc[key] = { test_id: p.test_id, test_code: p.test_code, items: [] };
        acc[key].items.push(p);
        return acc;
    }, {});

    const openAdd = (test_id) => {
        setEditId(null);
        setForm({ ...emptyForm, test_id: test_id || '' });
        setShowModal(true);
    };
    const openEdit = (p) => {
        setEditId(p.id);
        setForm({
            test_id: p.test_id, parameter_name: p.parameter_name, display_order: p.display_order,
            unit: p.unit || '', decimal_places: p.decimal_places,
            range_low: p.range_low ?? '', range_high: p.range_high ?? '',
            range_low_male: p.range_low_male ?? '', range_high_male: p.range_high_male ?? '',
            range_low_female: p.range_low_female ?? '', range_high_female: p.range_high_female ?? '',
            critical_low: p.critical_low ?? '', critical_high: p.critical_high ?? '',
            is_qualitative: p.is_qualitative, qualitative_normal: p.qualitative_normal || '',
            method: p.method || ''
        });
        setShowModal(true);
    };

    const toggleActive = async (p) => {
        try {
            await labAPI.updateParameter(p.id, { ...p, is_active: !p.is_active });
            toast.success(p.is_active ? 'Parameter deactivated' : 'Parameter activated');
            load();
        } catch { toast.error('Failed to update'); }
    };

    const save = async () => {
        if (!form.parameter_name || !form.test_id) return toast.error('Parameter name and test are required');
        setSaving(true);
        try {
            if (editId) await labAPI.updateParameter(editId, form);
            else await labAPI.addParameter(form);
            toast.success(editId ? 'Parameter updated' : 'Parameter added');
            setShowModal(false);
            load();
        } catch { toast.error('Failed to save'); }
        setSaving(false);
    };

    const f = (field) => ({ value: form[field], onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });
    const numF = (field) => ({ value: form[field], onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

    const testsInCategory = tests.filter(t => t.category === activeCategory);

    const cell = { padding: '8px 12px', fontSize: 13 };
    const th = { padding: '8px 12px', fontSize: 12, fontWeight: 700, color: '#fff', background: '#3C5A6B', textAlign: 'left' };

    return (
        <div style={{ maxWidth: 1200 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1e293b' }}>⚙️ Parameter Settings</h2>
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Configure reference ranges and critical values for each test parameter</p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search parameter..."
                        style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 200 }} />
                    <button onClick={() => openAdd('')}
                        style={{ padding: '8px 18px', background: '#3C5A6B', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                        + Add Parameter
                    </button>
                </div>
            </div>

            {/* Category Tabs */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
                {CATEGORIES.map(cat => (
                    <button key={cat} onClick={() => setActiveCategory(cat)}
                        style={{ padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                            background: activeCategory === cat ? '#3C5A6B' : '#f1f5f9',
                            color: activeCategory === cat ? '#fff' : '#475569' }}>
                        {cat}
                        <span style={{ marginLeft: 6, background: activeCategory === cat ? 'rgba(255,255,255,0.3)' : '#e2e8f0',
                            color: activeCategory === cat ? '#fff' : '#64748b',
                            borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>
                            {params.filter(p => p.category === cat).length}
                        </span>
                    </button>
                ))}
            </div>

            {/* Tables grouped by test */}
            {Object.keys(grouped).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                    <div style={{ fontSize: 40 }}>🔬</div>
                    <div style={{ marginTop: 12, fontSize: 15 }}>No parameters configured for {activeCategory}</div>
                    <div style={{ fontSize: 12, marginTop: 6 }}>Click "Add Parameter" to get started</div>
                </div>
            ) : (
                Object.entries(grouped).map(([testName, group]) => (
                    <div key={testName} style={{ marginBottom: 28, background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <span style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>
                                {testName} <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 12 }}>({group.test_code})</span>
                            </span>
                            <button onClick={() => openAdd(group.test_id)}
                                style={{ padding: '5px 12px', background: '#3C5A6B', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                + Add
                            </button>
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        {['#','Parameter','Unit','Normal Range','Male Range','Female Range','Critical','Method',''].map(h => (
                                            <th key={h} style={th}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map((p, idx) => (
                                        <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                                            <td style={{ ...cell, color: '#94a3b8', width: 32 }}>{p.display_order}</td>
                                            <td style={{ ...cell, fontWeight: 600, color: '#1e293b' }}>{p.parameter_name}</td>
                                            <td style={{ ...cell, color: '#64748b' }}>{p.unit || '—'}</td>
                                            <td style={{ ...cell }}>
                                                {p.is_qualitative
                                                    ? <span style={{ color: '#7c3aed', fontSize: 12 }}>Qualitative: {p.qualitative_normal}</span>
                                                    : (p.range_low != null && p.range_high != null)
                                                        ? <span style={{ color: '#16a34a', fontWeight: 600 }}>{p.range_low} – {p.range_high}</span>
                                                        : '—'}
                                            </td>
                                            <td style={{ ...cell, color: '#2563eb', fontSize: 12 }}>
                                                {p.range_low_male != null ? `${p.range_low_male}–${p.range_high_male}` : '—'}
                                            </td>
                                            <td style={{ ...cell, color: '#db2777', fontSize: 12 }}>
                                                {p.range_low_female != null ? `${p.range_low_female}–${p.range_high_female}` : '—'}
                                            </td>
                                            <td style={{ ...cell, fontSize: 12 }}>
                                                {(p.critical_low != null || p.critical_high != null) ? (
                                                    <span style={{ color: '#dc2626', fontWeight: 600 }}>
                                                        {p.critical_low != null ? `↓${p.critical_low}` : ''}{p.critical_low != null && p.critical_high != null ? ' ' : ''}{p.critical_high != null ? `↑${p.critical_high}` : ''}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td style={{ ...cell, color: '#64748b', fontSize: 12 }}>{p.method || '—'}</td>
                                            <td style={{ ...cell }}>
                                                <div style={{ display: 'flex', gap: 6 }}>
                                                    <button onClick={() => openEdit(p)}
                                                        style={{ padding: '3px 10px', background: '#e0f2fe', color: '#0369a1', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                        Edit
                                                    </button>
                                                    <button onClick={() => toggleActive(p)}
                                                        style={{ padding: '3px 10px', background: p.is_active ? '#fef2f2' : '#f0fdf4', color: p.is_active ? '#dc2626' : '#16a34a', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                        {p.is_active ? 'Disable' : 'Enable'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                    <div style={{ background: '#fff', borderRadius: 12, width: 680, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
                        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 700, fontSize: 16 }}>{editId ? 'Edit Parameter' : 'Add Parameter'}</span>
                            <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
                        </div>
                        <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            {/* Test Select */}
                            <div style={{ gridColumn: '1/-1' }}>
                                <label style={lbl}>Test</label>
                                <select style={inp} value={form.test_id} onChange={e => setForm(p => ({ ...p, test_id: e.target.value }))}>
                                    <option value="">— Select Test —</option>
                                    {tests.map(t => <option key={t.id} value={t.id}>{t.test_name} ({t.test_code})</option>)}
                                </select>
                            </div>
                            <div style={{ gridColumn: '1/-1' }}>
                                <label style={lbl}>Parameter Name *</label>
                                <input style={inp} placeholder="e.g. Haemoglobin" {...f('parameter_name')} />
                            </div>
                            <div>
                                <label style={lbl}>Unit</label>
                                <input style={inp} placeholder="g/dL, mg/dL, IU/L" {...f('unit')} />
                            </div>
                            <div>
                                <label style={lbl}>Decimal Places</label>
                                <input style={inp} type="number" min="0" max="4" {...f('decimal_places')} />
                            </div>
                            <div>
                                <label style={lbl}>Display Order</label>
                                <input style={inp} type="number" {...f('display_order')} />
                            </div>
                            <div>
                                <label style={lbl}>Method</label>
                                <input style={inp} placeholder="Colorimetric, Enzymatic..." {...f('method')} />
                            </div>

                            {/* Qualitative toggle */}
                            <div style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 10 }}>
                                <input type="checkbox" id="qualitative" checked={form.is_qualitative}
                                    onChange={e => setForm(p => ({ ...p, is_qualitative: e.target.checked }))} />
                                <label htmlFor="qualitative" style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                                    Qualitative test (Positive/Negative, Reactive/Non-Reactive)
                                </label>
                            </div>

                            {form.is_qualitative ? (
                                <div style={{ gridColumn: '1/-1' }}>
                                    <label style={lbl}>Normal Result</label>
                                    <input style={inp} placeholder="Non-Reactive, Negative..." {...f('qualitative_normal')} />
                                </div>
                            ) : (
                                <>
                                    <div style={{ gridColumn: '1/-1' }}>
                                        <div style={sectionTitle}>General Normal Range</div>
                                    </div>
                                    <div>
                                        <label style={lbl}>Normal Low</label>
                                        <input style={inp} type="number" step="any" placeholder="Min" {...numF('range_low')} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Normal High</label>
                                        <input style={inp} type="number" step="any" placeholder="Max" {...numF('range_high')} />
                                    </div>

                                    <div style={{ gridColumn: '1/-1' }}>
                                        <div style={sectionTitle}>Male-Specific Range (optional)</div>
                                    </div>
                                    <div>
                                        <label style={lbl}>Male Low</label>
                                        <input style={inp} type="number" step="any" placeholder="Min" {...numF('range_low_male')} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Male High</label>
                                        <input style={inp} type="number" step="any" placeholder="Max" {...numF('range_high_male')} />
                                    </div>

                                    <div style={{ gridColumn: '1/-1' }}>
                                        <div style={sectionTitle}>Female-Specific Range (optional)</div>
                                    </div>
                                    <div>
                                        <label style={lbl}>Female Low</label>
                                        <input style={inp} type="number" step="any" placeholder="Min" {...numF('range_low_female')} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Female High</label>
                                        <input style={inp} type="number" step="any" placeholder="Max" {...numF('range_high_female')} />
                                    </div>

                                    <div style={{ gridColumn: '1/-1' }}>
                                        <div style={sectionTitle}>Critical Values (alert when exceeded)</div>
                                    </div>
                                    <div>
                                        <label style={lbl}>Critical Low</label>
                                        <input style={{ ...inp, borderColor: '#fca5a5' }} type="number" step="any" placeholder="Alert below" {...numF('critical_low')} />
                                    </div>
                                    <div>
                                        <label style={lbl}>Critical High</label>
                                        <input style={{ ...inp, borderColor: '#fca5a5' }} type="number" step="any" placeholder="Alert above" {...numF('critical_high')} />
                                    </div>
                                </>
                            )}
                        </div>
                        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                            <button onClick={() => setShowModal(false)}
                                style={{ padding: '8px 20px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 600 }}>
                                Cancel
                            </button>
                            <button onClick={save} disabled={saving}
                                style={{ padding: '8px 24px', background: '#3C5A6B', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
                                {saving ? 'Saving...' : 'Save Parameter'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 };
const inp = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', outline: 'none' };
const sectionTitle = { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, padding: '4px 0', borderBottom: '1px solid #f1f5f9', marginBottom: 4 };
